import { useMemo, useState } from 'react';
import {
  ActionButton,
  Button,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  MenuItem,
  Stack,
  StatusChip,
  TagChip,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { SIGNABLE_KINDS } from '@munifocus/access';
import {
  useGrantSigningAuthority,
  useRevokeSigningAuthority,
  useSigningAuthority,
  useSigningCandidates,
} from '../../lib/administration-api';
import { useScope } from '../../providers/ScopeProvider';
import { useMutationToast } from '../../lib/mutation-toast';
import { useConfirmAction } from '../../lib/useConfirmAction';

/**
 * Who may sign which instrument at this municipality.
 *
 * The table merges the role defaults with the explicit grants and says which is
 * which, because a screen showing only the explicit rows would read as "nobody
 * can sign anything" at a municipality that has never edited its defaults —
 * which is most of them.
 *
 * Signing is deliberately not a permission: it is an attestation about a named
 * human on a legal instrument, and it has to stay answerable long after the
 * account is gone. Revoking authority stops the *next* document; it does not
 * unmake one already served.
 */
export function SigningAuthorityPanel() {
  const { t } = useTranslation();
  const toast = useMutationToast();
  const { active, can } = useScope();
  const confirm = useConfirmAction();
  const localGovernmentId = active?.kind === 'local_government' ? active.id : null;

  const rows = useSigningAuthority(localGovernmentId);
  // Seats *and* appointed officers. Reading only the seats is why this picker
  // was empty at every municipality whose enforcement is done by a contractor,
  // while the table below happily listed those officers' role defaults.
  const candidates = useSigningCandidates(localGovernmentId);
  const grant = useGrantSigningAuthority(localGovernmentId ?? '');
  const revoke = useRevokeSigningAuthority(localGovernmentId ?? '');

  const [granting, setGranting] = useState(false);
  const [userId, setUserId] = useState('');
  const [documentKind, setDocumentKind] = useState<string>('notice-to-comply');
  const [effect, setEffect] = useState<'allow' | 'deny'>('allow');

  /** Grouped per person: the question is "what may Daniel sign", not a flat list. */
  const byPerson = useMemo(() => {
    const map = new Map<string, { name: string; rows: NonNullable<typeof rows.data> }>();
    for (const row of rows.data ?? []) {
      const entry = map.get(row.userId) ?? { name: row.userName, rows: [] };
      entry.rows.push(row);
      map.set(row.userId, entry);
    }
    return [...map.entries()];
  }, [rows.data]);

  /**
   * Resolves whether the action landed, so a dialog can stay open — with what
   * was typed still in it — when it did not. Closing and clearing after a failed
   * save threw the officer's input away behind an error toast.
   */
  const run = async (successKey: string, fn: () => Promise<unknown>): Promise<boolean> => {
    try {
      await fn();
      toast.success(successKey);
      return true;
    } catch (err) {
      toast.error(err, 'gov.admin.errors.actionFailed');
      return false;
    }
  };

  if (!localGovernmentId) {
    return (
      <HairlineCard sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('gov.admin.signing.pickMunicipality')}
        </Typography>
      </HairlineCard>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t('gov.admin.signing.help')}
      </Typography>

      <DataTable
        rows={byPerson}
        getRowKey={([id]) => id}
        isLoading={rows.isLoading}
        emptyLabel={t('gov.admin.signing.empty')}
        initialSortKey="person"
        search={{
          placeholder: t('gov.admin.signing.searchPlaceholder'),
          of: ([, entry]) => entry.name,
        }}
        action={
          can('signing:grant') ? (
            <ActionButton
              label={t('gov.admin.signing.grant')}
              icon={Icons.Plus}
              onClick={() => setGranting(true)}
            />
          ) : null
        }
        columns={[
          {
            key: 'person',
            label: t('gov.admin.signing.person'),
            primary: true,
            nowrap: true,
            sortValue: ([, entry]) => entry.name,
            cell: ([, entry]) => entry.name,
          },
          {
            key: 'maySign',
            label: t('gov.admin.signing.maySign'),
            sortValue: ([, entry]) => entry.rows.length,
            cell: ([, entry]) => {
              // A chip with an X on it is one slip away from removing
              // someone's authority over a legal instrument, and the row gives
              // no hint that it is about to happen.
              const revokeHandler = (row: (typeof entry.rows)[number]) =>
                can('signing:revoke') && !row.fromRoleDefault
                  ? async () => {
                      if (
                        !(await confirm({
                          title: t('gov.admin.signing.revokeTitle'),
                          body: t('gov.admin.signing.revokeBody', {
                            name: entry.name,
                            instrument: t(`gov.admin.signing.kinds.${row.documentKind}`),
                          }),
                          confirmLabel: t('gov.admin.signing.revokeConfirm'),
                          tone: 'danger',
                        }))
                      ) {
                        return;
                      }
                      await run('gov.admin.signing.revoked', () =>
                        revoke.mutateAsync({
                          userId: row.userId,
                          documentKind: row.documentKind,
                        }),
                      );
                    }
                  : undefined;

              return (
                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  {entry.rows.map((row) =>
                    // Three facts, two components. A denial is a state to act
                    // on, so it is a status; a grant is a fact about the row,
                    // so it is a tag. Emphasis carries the third — whether this
                    // municipality decided it or inherited it from the role.
                    row.effect === 'deny' ? (
                      <StatusChip
                        key={row.documentKind}
                        status="rejected"
                        label={t(`gov.admin.signing.kinds.${row.documentKind}`)}
                        onDelete={revokeHandler(row)}
                      />
                    ) : (
                      <TagChip
                        key={row.documentKind}
                        emphasis={!row.fromRoleDefault}
                        label={t(`gov.admin.signing.kinds.${row.documentKind}`)}
                        onDelete={revokeHandler(row)}
                      />
                    ),
                  )}
                </Stack>
              );
            },
          },
        ]}
      />

      <Dialog open={granting} onClose={() => setGranting(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.admin.signing.grant')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label={t('gov.admin.signing.person')}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              fullWidth
              required
              helperText={
                candidates.isLoading
                  ? undefined
                  : (candidates.data ?? []).length === 0
                    ? t('gov.admin.signing.noCandidates')
                    : t('gov.admin.signing.personHelp')
              }
            >
              {/* Keyed by person *and* role: holding two seats at one
                  municipality is legal and seeded, and two identical rows would
                  be a coin toss. The value stays the user id — authority is
                  granted to a person, not to a seat. */}
              {(candidates.data ?? []).map((candidate) => (
                <MenuItem key={`${candidate.userId}:${candidate.role}`} value={candidate.userId}>
                  {candidate.userName} — {t(`gov.admin.roles.${candidate.role}`)}
                  {candidate.title ? ` (${candidate.title})` : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('gov.admin.signing.instrument')}
              value={documentKind}
              onChange={(e) => setDocumentKind(e.target.value)}
              fullWidth
            >
              {SIGNABLE_KINDS.map((kind) => (
                <MenuItem key={kind} value={kind}>
                  {t(`gov.admin.signing.kinds.${kind}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('gov.admin.signing.effect')}
              value={effect}
              onChange={(e) => setEffect(e.target.value as 'allow' | 'deny')}
              fullWidth
              helperText={t('gov.admin.signing.effectHelp')}
            >
              <MenuItem value="allow">{t('gov.admin.signing.effects.allow')}</MenuItem>
              <MenuItem value="deny">{t('gov.admin.signing.effects.deny')}</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGranting(false)}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!userId || grant.isPending}
            onClick={async () => {
              const ok = await run('gov.admin.signing.granted', () =>
                grant.mutateAsync({ userId, documentKind: documentKind as never, effect }),
              );
              if (!ok) return;
              setGranting(false);
              setUserId('');
            }}
          >
            {t('common.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
